import { Currency } from "@redotech/money/currencies";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  FulfillmentOrderAddressSchema,
  type FulfillmentOrderAddress,
  type IFulfillmentOrderAddress,
} from "../fulfillments/fulfillment-order-address";
import { FreightLocationType, HandlingUnitType } from "./freight";

const PickupRequirementsSchema_ = z.object({
  liftgateRequired: z.boolean(),
  insidePickup: z.boolean(),
  limitedAccessLocation: z.boolean(),
});
export const PickupRequirementsSchema: z.ZodType<PickupRequirements> =
  PickupRequirementsSchema_;
export interface PickupRequirements
  extends z.infer<typeof PickupRequirementsSchema_> {}

const DeliveryRequirementsSchema_ = z.object({
  liftgateRequired: z.boolean(),
  insideDelivery: z.boolean(),
  limitedAccessLocation: z.boolean(),
  appointmentRequired: z.boolean(),
});
export const DeliveryRequirementsSchema: z.ZodType<DeliveryRequirements> =
  DeliveryRequirementsSchema_;
export interface DeliveryRequirements
  extends z.infer<typeof DeliveryRequirementsSchema_> {}

const FreightDimensionsSchema_ = z.object({
  length: z.number(),
  width: z.number(),
  height: z.number(),
});
export const FreightDimensionsSchema: z.ZodType<FreightDimensions> =
  FreightDimensionsSchema_;
export interface FreightDimensions
  extends z.infer<typeof FreightDimensionsSchema_> {}

const FreightShippedItemSchema_ = z.object({
  description: z.string(),
  freightClass: z.number(),
  quantity: z.number(),
  weightLbs: z.number(),
  dimensions: FreightDimensionsSchema.nullish(),
});
export const FreightShippedItemSchema: z.ZodType<FreightShippedItem> =
  FreightShippedItemSchema_;
export interface FreightShippedItem
  extends z.infer<typeof FreightShippedItemSchema_> {}

const FreightHandlingUnitSchema_ = z.object({
  type: createEnum(HandlingUnitType, { enumName: "HandlingUnitType" }),
  quantity: z.number(),
  stackable: z.boolean(),
  totalWeightLbs: z.number(),
  dimensions: FreightDimensionsSchema.nullish(),
  shippedItems: z.array(FreightShippedItemSchema),
});
export const FreightHandlingUnitSchema: z.ZodType<FreightHandlingUnit> =
  FreightHandlingUnitSchema_;
export interface FreightHandlingUnit
  extends z.infer<typeof FreightHandlingUnitSchema_> {}

const FreightQuoteRequestSchema_ = z.object({
  originAddress:
    FulfillmentOrderAddressSchema as IFulfillmentOrderAddress as z.ZodType<FulfillmentOrderAddress>,
  destinationAddress:
    FulfillmentOrderAddressSchema as IFulfillmentOrderAddress as z.ZodType<FulfillmentOrderAddress>,
  pickupDateTime: z.string(),
  pickupLocationType: createEnum(FreightLocationType, {
    enumName: "FreightLocationType",
  }).nullish(),
  deliveryLocationType: createEnum(FreightLocationType, {
    enumName: "FreightLocationType",
  }).nullish(),
  pickupRequirements: PickupRequirementsSchema,
  deliveryRequirements: DeliveryRequirementsSchema,
  pickupInstructions: z.string().nullish(),
  deliveryInstructions: z.string().nullish(),
  handlingUnitList: z.array(FreightHandlingUnitSchema),
  customerReference1: z.string().nullish(),
  customerReference2: z.string().nullish(),
  specialInstructions: z.string().nullish(),
});
export const FreightQuoteRequestSchema: z.ZodType<FreightQuoteRequest> =
  FreightQuoteRequestSchema_;
export interface FreightQuoteRequest
  extends z.infer<typeof FreightQuoteRequestSchema_> {}

const FreightQuoteSchema_ = z.object({
  quoteId: z.string(),
  productTransactionId: z.string(),
  scac: z.string(),
  carrierName: z.string(),
  serviceLevel: z.string(),
  estDeliveryDays: z.number().nullish(),
  estDeliveryDate: z.string().nullish(),
  quoteExpirationDate: z.string().nullish(),
  totalPrice: z.object({
    value: z.number(),
    unit: createEnum(Currency, { enumName: "Currency" }),
  }),
  priceBreakdown: z.array(
    z.object({
      price: z.object({
        value: z.number(),
        unit: createEnum(Currency, { enumName: "Currency" }),
      }),
      category: z.string(),
      description: z.string(),
    }),
  ),
});

export const FreightQuoteSchema: z.ZodType<FreightQuote> = FreightQuoteSchema_;
export interface FreightQuote extends z.infer<typeof FreightQuoteSchema_> {}
